import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import Slide from '@mui/material/Slide';
import {connect, useSelector} from 'react-redux';
import {useAuth} from '@apps-common/auth-react';

import AccessFilterToggle from './access-filter-toggle.jsx';
import Breadcrumbs from './breadcrumbs.jsx';
import DeselectMenuButton from '@explorer/components/sidebars/search/results-panel/deselect-menu-button';
import Footer from './footer.jsx';
import ResultsList from './results-list.jsx';
import ResultsListV2 from './results-list-v2/index.jsx';
import ScenesList from './scenes-list.jsx';
import ScenesListV2 from './scenes-list-v2/index.jsx';
import SortButton from '@explorer/components/sidebars/sort-button';
import {LABS_MAP, MODALS} from '@explorer/constants';
import {LoadingMessage} from '@explorer/components/ui/message-container';
import {selectFilteredResultsList} from '@explorer/selectors/results';
import {
  setResultsSortDirectionAndPageNumber,
  setScenesSortDirection,
} from '@explorer/actions/sidebar';
import {showModal} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';
import {userCanDownload} from '@explorer/util/user.js';

const DailyResultsPanel = ({activeResult, ...props}) => {
  const auth = useAuth();
  const resultsCount = props.results.length;
  const subscriptions = useSelector(state => state.subscriptions.byId);
  const canDownload = useMemo(
    () => userCanDownload(auth.user?.claims, Object.values(subscriptions)),
    [auth.user?.claims, subscriptions]
  );

  const onRequestAccess = event => {
    event.stopPropagation();
    props.showModal(MODALS.SCENES_NO_ACCESS);
  };

  const onOrderClick = () => {
    trackEvent('Orders Dialog - Launch', {
      itemCount: props.orderableSelectedItems.length,
    });
    props.showModal(MODALS.ORDER);
  };

  if (props.resultsLoading && resultsCount === 0) {
    return <LoadingMessage suffix="results" />;
  }

  return (
    <>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1}
        flexShrink={0}
      >
        {!auth.user?.claims.is_trial && !activeResult && <AccessFilterToggle />}
        <Breadcrumbs count={resultsCount} />
        {props.useListv2 && !activeResult && (
          <Box marginRight={0.5}>
            <DeselectMenuButton />
          </Box>
        )}
        <SortButton
          sortDirection={
            activeResult
              ? props.scenesSortDirection
              : props.resultsSortDirection
          }
          setDirection={
            activeResult
              ? props.setScenesSortDirection
              : props.setResultsSortDirectionAndPageNumber
          }
        />
      </Box>
      <Divider />
      <Box height="100%" position="relative" overflow="hidden">
        <Slide in={!activeResult} direction="left">
          <Box height={!activeResult ? '100%' : 0}>
            {props.useListv2 ? (
              <ResultsListV2 canDownload={canDownload} />
            ) : (
              <ResultsList
                results={props.results}
                resultsLoading={props.resultsLoading}
                onRequestAccess={onRequestAccess}
              />
            )}
          </Box>
        </Slide>
        <Slide in={Boolean(activeResult)} direction="right">
          <Box height={activeResult ? '100%' : 0} overflow="auto">
            {activeResult &&
              (!props.useListv2 ? (
                <ScenesList
                  activeResult={activeResult}
                  onRequestAccess={onRequestAccess}
                />
              ) : (
                <ScenesListV2
                  resultId={activeResult?.id}
                  canDownload={canDownload}
                />
              ))}
          </Box>
        </Slide>
      </Box>
      <Footer
        count={props.orderableSelectedItems.length}
        openApiDialog={() => props.showModal(MODALS.API)}
        onOrderClick={() => onOrderClick()}
      />
    </>
  );
};

DailyResultsPanel.propTypes = {
  activeResult: PropTypes.object,
  orderableSelectedItems: PropTypes.array,
  results: PropTypes.array.isRequired,
  resultsLoading: PropTypes.bool,
  showFiltersPanel: PropTypes.func,
  showModal: PropTypes.func,
  searchName: PropTypes.string,
  resultsSortDirection: PropTypes.string,
  scenesSortDirection: PropTypes.string,
  setResultsSortDirectionAndPageNumber: PropTypes.func,
  setScenesSortDirection: PropTypes.func,
  useListv2: PropTypes.bool,
};

const mapStateToProps = state => ({
  activeResult: state.results.resultsById[state.sidebar.activeResultId],
  orderableSelectedItems: state.results.orderItems,
  results: selectFilteredResultsList(state),
  resultsLoading: state.results.loading,
  searchName: state.search.name,
  resultsSortDirection: state.sidebar.resultsSortDirection,
  scenesSortDirection: state.sidebar.scenesSortDirection,
  useListv2: !state.labs[LABS_MAP.SINGLE_ACTION],
});

const mapDispatchProps = {
  showModal,
  setResultsSortDirectionAndPageNumber,
  setScenesSortDirection,
};

export default connect(mapStateToProps, mapDispatchProps)(DailyResultsPanel);
